import { useEffect, useCallback } from 'react';
import type { Map, GeoJSONSource, ExpressionSpecification } from 'mapbox-gl';
import type { Unit } from '../../../types';
import { ZOOM_THRESHOLDS, MARKER_STYLES, STATUS_COLORS, UNIT_ICON_SVG, CLUSTER_CONFIG } from '../mapConfig';

// Status × industry combinations for composite marker images
const STATUSES = ['online', 'idle', 'offline', 'unknown'] as const;
const INDUSTRIES = ['film', 'relief', 'event', 'gov', 'utility'] as const;

/**
 * Generate a composite marker image: colored circle + white icon as a single bitmap.
 * This ensures the circle background and icon render as one atomic unit in Mapbox,
 * preventing z-index issues where icons from one marker overlap another marker's circle.
 */
function generateCompositeMarkerImage(
  fillColor: string,
  strokeColor: string,
  strokeWidth: number,
  radius: number,
  iconSvg: string,
): Promise<HTMLImageElement> {
  const size = (radius + strokeWidth) * 2;
  const pixelRatio = 2; // Retina support
  const canvasSize = size * pixelRatio;

  const canvas = document.createElement('canvas');
  canvas.width = canvasSize;
  canvas.height = canvasSize;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(pixelRatio, pixelRatio);

  const center = size / 2;

  // Draw filled circle with stroke
  ctx.beginPath();
  ctx.arc(center, center, radius, 0, Math.PI * 2);
  ctx.fillStyle = fillColor;
  ctx.fill();
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = strokeWidth;
  ctx.stroke();

  // Draw icon SVG in center
  return new Promise((resolve) => {
    const iconImg = new Image();
    iconImg.onload = () => {
      // Draw icon centered, scaled to fit inside the circle
      const iconSize = radius * 1.2; // Icon slightly smaller than circle
      const iconOffset = center - iconSize / 2;
      ctx.drawImage(iconImg, iconOffset, iconOffset, iconSize, iconSize);

      // Convert canvas to image
      const resultImg = new Image(size, size);
      resultImg.onload = () => resolve(resultImg);
      resultImg.src = canvas.toDataURL();
    };
    iconImg.src = 'data:image/svg+xml;base64,' + btoa(iconSvg);
  });
}

// Industry-specific icons (full size for zoom 6+)
const ICONS = {
  FILM: UNIT_ICON_SVG,
  RELIEF: `
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2C17.5228 6.47715 22 12 22Z" stroke="white" stroke-width="2" fill="none"/>
      <path d="M8 12H16M12 8V16" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `,
  EVENT: `
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 18V5l12-2v13" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <circle cx="6" cy="18" r="3" stroke="white" stroke-width="2"/>
      <circle cx="18" cy="16" r="3" stroke="white" stroke-width="2"/>
    </svg>
  `,
  GOV: `
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1v12z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M4 22v-7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `,
  UTILITY: `
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `,
};

// Thin stroke icons for small dots (zoom 3-5.5)
const ICONS_THIN = {
  FILM: `
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" stroke="white" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M15 18H9" stroke="white" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M19 18h2a1 1 0 0 0 1-1v-3.28a1 1 0 0 0-.684-.948l-1.923-.641a1 1 0 0 1-.578-.579l-1.5-4.5A1 1 0 0 0 16.382 6H14" stroke="white" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
      <circle cx="7" cy="18" r="2" stroke="white" stroke-width="1" fill="white"/>
      <circle cx="17" cy="18" r="2" stroke="white" stroke-width="1" fill="white"/>
    </svg>
  `,
  RELIEF: `
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="white" stroke-width="1" fill="none"/>
      <path d="M8 12H16M12 8V16" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `,
  EVENT: `
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 18V5l12-2v13" stroke="white" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
      <circle cx="6" cy="18" r="3" stroke="white" stroke-width="1"/>
      <circle cx="18" cy="16" r="3" stroke="white" stroke-width="1"/>
    </svg>
  `,
  GOV: `
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1v12z" stroke="white" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M4 22v-7" stroke="white" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `,
  UTILITY: `
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="white" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `,
};

/**
 * Map Layers Management Hook
 *
 * Handles creation, configuration, and management of all map layers:
 * - Small dots (zoom 0-9, unclustered)
 * - Clusters (zoom 9+, clustered source)
 * - Full unit markers (zoom 9+)
 * - Hover highlighting layer
 *
 * Performance: Layers created once, visibility toggled via minzoom/maxzoom
 */

interface UseMapLayersProps {
  map: Map | null;
  mapLoaded: boolean;
  sourcesReady: boolean;
  styleLoaded: number; // Incremented when style changes (satellite/road, light/dark)
  dotsSourceId: string; // Unclustered source for small dots
  sourceId: string; // Clustered source for markers and clusters
}

interface UseMapLayersReturn {
  loadIcons: () => void;
  updateHoverLayer: (unit: Unit | null, currentZoom: number, isClustered?: boolean) => void;
}

export function useMapLayers({
  map,
  mapLoaded,
  sourcesReady,
  styleLoaded,
  dotsSourceId,
  sourceId,
}: UseMapLayersProps): UseMapLayersReturn {
  // Load all icons: individual SVGs + composite marker images (circle + icon)
  const loadIcons = useCallback(() => {
    if (!map) return;

    const loadIcon = (name: string, svg: string) => {
      if (map.hasImage(name)) return;
      const img = new Image(48, 48);
      img.onload = () => {
        if (map && !map.hasImage(name)) {
          map.addImage(name, img);
        }
      };
      img.src = 'data:image/svg+xml;base64,' + btoa(svg);
    };

    // Regular icons (kept for cluster popups and other uses)
    loadIcon('icon-film', ICONS.FILM);
    loadIcon('icon-relief', ICONS.RELIEF);
    loadIcon('icon-event', ICONS.EVENT);
    loadIcon('icon-gov', ICONS.GOV);
    loadIcon('icon-utility', ICONS.UTILITY);

    // Thin stroke icons (for small dots zoom 3-5.5)
    loadIcon('icon-film-thin', ICONS_THIN.FILM);
    loadIcon('icon-relief-thin', ICONS_THIN.RELIEF);
    loadIcon('icon-event-thin', ICONS_THIN.EVENT);
    loadIcon('icon-gov-thin', ICONS_THIN.GOV);
    loadIcon('icon-utility-thin', ICONS_THIN.UTILITY);

    // Fallback
    loadIcon('unit-icon', ICONS.FILM);

    // Generate composite marker images at two sizes: medium (zoom 10-14) and large (zoom 14+)
    // Each size × status × industry = 20 images per size = 40 total
    const statusColorMap: Record<string, string> = {
      online: STATUS_COLORS.online.light,
      idle: STATUS_COLORS.idle.light,
      offline: STATUS_COLORS.offline.light,
      unknown: STATUS_COLORS.default,
    };
    const industryIconMap: Record<string, string> = {
      film: ICONS.FILM,
      relief: ICONS.RELIEF,
      event: ICONS.EVENT,
      gov: ICONS.GOV,
      utility: ICONS.UTILITY,
    };

    const markerSizes = [
      { prefix: 'marker-md', ...MARKER_STYLES.mediumMarker },
      { prefix: 'marker-lg', ...MARKER_STYLES.fullMarker },
    ];

    for (const size of markerSizes) {
      for (const status of STATUSES) {
        for (const industry of INDUSTRIES) {
          const name = `${size.prefix}-${status}-${industry}`;
          if (map.hasImage(name)) continue;

          generateCompositeMarkerImage(
            statusColorMap[status],
            size.strokeColor,
            size.strokeWidth,
            size.radius,
            industryIconMap[industry],
          ).then((img) => {
            if (map && !map.hasImage(name)) {
              map.addImage(name, img, { pixelRatio: 2 });
            }
          });
        }
      }
    }
  }, [map]);

  // Create all map layers (dots, clusters, composite markers)
  // LAYER Z-INDEX STRATEGY:
  // Layers render in the order they're added:
  // 1. Small dots (zoom 0-9) - unclustered from dots source
  // 2. Clusters (zoom 9-16) + count labels - from clustered source
  // 3. Composite markers (zoom 9+) - single symbol layer with baked-in circle + icon
  //    This prevents z-index issues between circle and icon layers
  useEffect(() => {
    if (!map || !mapLoaded || !sourcesReady) return;

    let timeoutId: NodeJS.Timeout;

    const addLayers = () => {
      if (!map) return;

      // CRITICAL: Ensure map style is loaded before checking layers
      if (!map.isStyleLoaded() || !map.getStyle()) {
        timeoutId = setTimeout(addLayers, 100);
        return;
      }

      // Check if layers already exist
      if (map.getLayer('unclustered-dots')) return;

      // CRITICAL: Wait for both sources to exist before adding layers
      if (!map.getSource(dotsSourceId) || !map.getSource(sourceId)) {
         // If sources are missing but we think they are ready, retry
         // In demo mode, sources might take longer to populate
         timeoutId = setTimeout(addLayers, 100);
         return;
      }

      loadIcons();

      try {
        // === Z-INDEX LAYER 1: SMALL DOTS (Zoom 0-7) ===
        // Individual unclustered markers from dots source (no clustering)
        if (!map.getLayer('unclustered-dots')) {
          map.addLayer({
            id: 'unclustered-dots',
            type: 'circle',
            source: dotsSourceId,
            minzoom: ZOOM_THRESHOLDS.SMALL_DOTS_MIN, // Start at zoom 0
            maxzoom: ZOOM_THRESHOLDS.SMALL_DOTS_MAX, // End at zoom 10
            paint: {
              'circle-color': [
                'match',
                ['get', 'status'],
                'online', STATUS_COLORS.online.dark,
                'idle', STATUS_COLORS.idle.dark,
                'offline', STATUS_COLORS.offline.dark,
                STATUS_COLORS.default,
              ],
              'circle-radius': [
                'interpolate',
                ['linear'],
                ['zoom'],
                ...MARKER_STYLES.smallDot.zoomScale.flatMap(s => [s.zoom, s.radius]),
              ],
              'circle-stroke-width': MARKER_STYLES.smallDot.strokeWidth,
              'circle-stroke-color': MARKER_STYLES.smallDot.strokeColor,
              'circle-opacity': MARKER_STYLES.smallDot.opacity,
              'circle-stroke-opacity': 1.0,
            },
          });
        }

        // === HIT AREA: Invisible circle layers for hover/click detection ===
        // Symbol layers only detect hover on rendered pixels. These transparent circles
        // provide a generous hit area underneath the symbol layers.
        if (!map.getLayer('clusters-hitarea')) {
          map.addLayer({
            id: 'clusters-hitarea',
            type: 'circle',
            source: sourceId,
            filter: ['has', 'point_count'],
            minzoom: CLUSTER_CONFIG.clusters.minZoom ?? ZOOM_THRESHOLDS.FULL_MARKERS_MIN,
            maxzoom: 16,
            paint: {
              'circle-radius': [
                'step',
                ['get', 'point_count'],
                ...MARKER_STYLES.cluster.sizes.flatMap(s => [s.radius, s.maxCount]).slice(0, -1),
              ],
              'circle-color': 'transparent',
              'circle-stroke-width': 0,
            },
          });
        }

        if (!map.getLayer('markers-hitarea')) {
          map.addLayer({
            id: 'markers-hitarea',
            type: 'circle',
            source: sourceId,
            filter: ['!', ['has', 'point_count']],
            minzoom: ZOOM_THRESHOLDS.MEDIUM_MARKERS_MIN,
            paint: {
              'circle-radius': [
                'step', ['zoom'],
                MARKER_STYLES.mediumMarker.radius, // zoom 10-17: medium hit area
                ZOOM_THRESHOLDS.FULL_MARKERS_MIN,
                MARKER_STYLES.fullMarker.radius, // zoom 17+: large hit area
              ],
              'circle-color': 'transparent',
              'circle-stroke-width': 0,
            },
          });
        }

        // === Z-INDEX LAYER 2: CLUSTERS (single symbol layer with circle bg + count text) ===
        // Combines circle background and count label into one symbol layer so they
        // render as a single unit — preventing text from overlapping other clusters.
        if (!map.getLayer('clusters')) {
          // Generate cluster circle images at each graduated size
          const clusterSizes = MARKER_STYLES.cluster.sizes;
          for (const sizeConfig of clusterSizes) {
            const imgName = `cluster-circle-${sizeConfig.radius}`;
            if (!map.hasImage(imgName)) {
              const diameter = sizeConfig.radius * 2;
              const pixelRatio = 2;
              const canvas = document.createElement('canvas');
              canvas.width = diameter * pixelRatio;
              canvas.height = diameter * pixelRatio;
              const ctx = canvas.getContext('2d')!;
              ctx.scale(pixelRatio, pixelRatio);
              // Draw filled circle
              ctx.beginPath();
              ctx.arc(sizeConfig.radius, sizeConfig.radius, sizeConfig.radius - MARKER_STYLES.cluster.strokeWidth, 0, Math.PI * 2);
              ctx.fillStyle = MARKER_STYLES.cluster.color;
              ctx.fill();
              ctx.strokeStyle = MARKER_STYLES.cluster.strokeColor;
              ctx.lineWidth = MARKER_STYLES.cluster.strokeWidth;
              ctx.stroke();
              // Convert to image and register
              const img = new Image(diameter, diameter);
              img.onload = () => {
                if (map && !map.hasImage(imgName)) {
                  map.addImage(imgName, img, { pixelRatio });
                }
              };
              img.src = canvas.toDataURL();
            }
          }

          // Build step expression for icon-image based on point_count
          // Maps point_count ranges to the correct sized cluster circle image
          const iconImageExpr: unknown[] = [
            'step',
            ['get', 'point_count'],
          ];
          for (let i = 0; i < clusterSizes.length; i++) {
            const s = clusterSizes[i];
            iconImageExpr.push(`cluster-circle-${s.radius}`);
            if (i < clusterSizes.length - 1) {
              iconImageExpr.push(s.maxCount + 1); // threshold for next size
            }
          }

          map.addLayer({
            id: 'clusters',
            type: 'symbol',
            source: sourceId,
            filter: ['has', 'point_count'],
            minzoom: CLUSTER_CONFIG.clusters.minZoom ?? ZOOM_THRESHOLDS.FULL_MARKERS_MIN,
            maxzoom: 16,
            layout: {
              'icon-image': iconImageExpr as unknown as ExpressionSpecification,
              'icon-size': 1,
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
              'text-field': '{point_count_abbreviated}',
              'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
              'text-size': [
                'step',
                ['get', 'point_count'],
                12,
                10,
                14,
                50,
                16,
              ],
              'text-allow-overlap': true,
              'text-ignore-placement': true,
            },
            paint: {
              'text-color': '#ffffff',
              'text-halo-color': 'rgba(0, 0, 0, 0.2)',
              'text-halo-width': 1,
            },
          });
        }

        // === Z-INDEX LAYER 3A: MEDIUM MARKERS (Zoom 10-14) ===
        // 25% larger than small dots, with icon
        if (!map.getLayer('unclustered-markers-md')) {
          map.addLayer({
            id: 'unclustered-markers-md',
            type: 'symbol',
            source: sourceId,
            filter: ['!', ['has', 'point_count']],
            minzoom: ZOOM_THRESHOLDS.MEDIUM_MARKERS_MIN,
            maxzoom: ZOOM_THRESHOLDS.MEDIUM_MARKERS_MAX,
            layout: {
              'icon-image': [
                'concat',
                'marker-md-',
                ['coalesce', ['get', 'status'], 'unknown'],
                '-',
                ['coalesce', ['get', 'industry_type'], 'film'],
              ],
              'icon-size': 1,
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
              'symbol-sort-key': ['get', 'id'],
            },
          });
        }

        // === Z-INDEX LAYER 3B: LARGE MARKERS (Zoom 14+) ===
        // Tooltip-sized markers for extreme close-ups
        if (!map.getLayer('unclustered-markers')) {
          map.addLayer({
            id: 'unclustered-markers',
            type: 'symbol',
            source: sourceId,
            filter: ['!', ['has', 'point_count']],
            minzoom: ZOOM_THRESHOLDS.FULL_MARKERS_MIN,
            layout: {
              'icon-image': [
                'concat',
                'marker-lg-',
                ['coalesce', ['get', 'status'], 'unknown'],
                '-',
                ['coalesce', ['get', 'industry_type'], 'film'],
              ],
              'icon-size': 1,
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
              'symbol-sort-key': ['get', 'id'],
            },
          });
        }
        
      } catch (e) {
        console.error('Error adding layers:', e);
        // Retry on error
        timeoutId = setTimeout(addLayers, 200);
      }
    };

    addLayers();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [map, mapLoaded, sourcesReady, styleLoaded, sourceId, dotsSourceId, loadIcons]);

  // Update hover layer (Hollywood Trucks green ring around hovered marker)
  const updateHoverLayer = useCallback((unit: Unit | null, currentZoom: number, isClustered: boolean = false) => {
    if (!map) return;

    // CRITICAL: Check if style is fully loaded (to prevent "Style is not done loading" errors)
    // Must be checked BEFORE calling getStyle()
    if (!map.isStyleLoaded()) return;

    if (!map.getStyle()) return;

    // Remove existing hover layer
    if (map.getLayer('hovered-marker')) {
      map.removeLayer('hovered-marker');
    }

    // Show hover at all zoom levels for dots, medium, and large markers (not clusters)
    if (unit && !isClustered) {
      const hoveredFeature: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [unit.location.lng, unit.location.lat],
          },
          properties: {},
        }],
      };

      if (map.getSource('hovered-unit')) {
        (map.getSource('hovered-unit') as GeoJSONSource).setData(hoveredFeature);
      } else {
        map.addSource('hovered-unit', {
          type: 'geojson',
          data: hoveredFeature,
        });
      }

      if (!map.getLayer('hovered-marker')) {
        // Size hover ring based on current zoom tier
        const hoverRadius = currentZoom >= ZOOM_THRESHOLDS.FULL_MARKERS_MIN
          ? MARKER_STYLES.hover.fullRadius
          : currentZoom >= ZOOM_THRESHOLDS.MEDIUM_MARKERS_MIN
            ? MARKER_STYLES.hover.mediumRadius
            : MARKER_STYLES.hover.dotRadius;

        map.addLayer({
          id: 'hovered-marker',
          type: 'circle',
          source: 'hovered-unit',
          paint: {
            'circle-radius': hoverRadius,
            'circle-color': MARKER_STYLES.hover.color,
            'circle-opacity': MARKER_STYLES.hover.opacity,
            'circle-stroke-width': MARKER_STYLES.hover.strokeWidth,
            'circle-stroke-color': MARKER_STYLES.hover.color,
          },
        });
      }
    } else if (map.getLayer('hovered-marker')) {
      map.removeLayer('hovered-marker');
      if (map.getSource('hovered-unit')) {
        map.removeSource('hovered-unit');
      }
    }
  }, [map]);

  return {
    loadIcons,
    updateHoverLayer,
  };
}
